import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TweenLite, Power3 } from 'gsap';
import { difference, last } from 'lodash';
import { addToArray } from '../../utils/immutable';
import './styles.styl';

const SwipeCards = ({ data, input, onInput, onCompletion }) => {
  let cardTitles = data.map(obj => obj.title);

  // Store the top card if this is the first visit
  if (!input) {
    onInput([cardTitles[0]]);
    return null;
  }

  let cardWrapperRef;

  const cardRefs = [];

  // We store titles but work with indexes within this
  // component so let's get the index of the current card
  const currentCardIndex = cardTitles.indexOf(last(input));

  const numCardsToShow = data.length < 5 ? data.length : 5;

  const handleClick = () => {
    storeNextCard();
    animateCards();
  };

  const storeNextCard = () => {
    let nextCardIndex = 0;

    if (currentCardIndex < data.length - 1) {
      nextCardIndex = currentCardIndex + 1;
    }

    const payload = addToArray(input, cardTitles[nextCardIndex]);

    onInput(payload);

    if (!difference(cardTitles, payload).length) {
      onCompletion();
    }
  };

  const animateCards = () => {
    const cards = [
      ...cardRefs.slice(currentCardIndex + 1),
      ...cardRefs.slice(0, currentCardIndex + 1)
    ];

    const cardClone = cardRefs[currentCardIndex].cloneNode(true);

    cardWrapperRef.appendChild(cardClone);

    const lastCardOffset = 5 * cardRefs.length;

    TweenLite.set(cardRefs[currentCardIndex], {
      opacity: 0,
      xPercent: 0,
      yPercent: 0,
      x: lastCardOffset,
      y: lastCardOffset,
      rotation: 0,
      zIndex: 1
    });

    // Animate the top card
    TweenLite.to(cardClone, 0.5, {
      xPercent: -50,
      yPercent: 25,
      rotation: -25,
      zIndex: cardRefs.length + 1,
      opacity: 0,
      ease: Power3.easeInOut,
      onComplete: () => {
        cardClone.remove();
      }
    });

    // Animate the card stack
    cards.forEach((card, i) => {
      TweenLite.to(card, 0.5, {
        x: '-=5',
        y: '-=5',
        zIndex: cardRefs.length - i,
        opacity: i < numCardsToShow ? 1 : 0,
        ease: Power3.easeInOut,
        delay: 0.1 * i
      });
    });
  };

  const handleKeyDown = e => {
    if (e.key === 'ArrowRight') {
      handleClick();
    }
  };

  const styles = i => ({
    transform: `translate(${5 * i}px, ${5 * i}px)`,
    opacity: i < numCardsToShow ? 1 : 0,
    zIndex: data.length - i
  });

  return (
    <div className="swipe-cards__wrapper" ref={w => (cardWrapperRef = w)}>
      {data.map((card, i) => (
        <div
          key={i}
          className="swipe-cards__card"
          ref={c => {
            cardRefs[i] = c;
          }}
          style={styles(i)}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex="-1"
          aria-hidden={i !== currentCardIndex}
        >
          <img
            alt={card.imageText}
            src={card.image}
            className={classNames('swipe-cards__image', 'img-center')}
          />
          <h2>{card.title}</h2>
          {card.body}
          <p className="swipe-cards__count">
            {i + 1} of {data.length}
          </p>
          <button
            className="swipe-cards__button"
            disabled={i !== currentCardIndex}
          >
            <span className="comet-screenreader-only">Next Card</span>
          </button>
        </div>
      ))}
    </div>
  );
};

SwipeCards.defaultProps = {
  input: null
};

SwipeCards.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      imageText: PropTypes.string,
      title: PropTypes.node,
      body: PropTypes.node
    })
  ).isRequired,
  input: PropTypes.arrayOf(PropTypes.node),
  onInput: PropTypes.func.isRequired,
  onCompletion: PropTypes.func.isRequired
};

export default SwipeCards;



// WEBPACK FOOTER //
// ../core/src/components/SwipeCards/SwipeCards.js